<template>
  <v-sheet id="ai-analysis-wrapper" class="ai-analysis mt-2 d-flex flex-column">
    <jobs-top-bar
      :search.sync="search"
      :status-filter.sync="statusFilter"
      :status-counts="statusCounts"
      :run-disabled-reason="runBlockedReason"
      :density.sync="density"
      :loading="jobsLoading"
      :polling="hasRunningJobs"
      @run="onRun"
      @refresh="reload" />
    <!-- Ticket 07: Run New Analysis (Full Scan, or the snaps of one AVA Inspect tag). -->
    <run-analysis-dialog
      v-model="runDialogOpen"
      :facility-name="runFacilityName"
      :blocked-reason="runBlockedReason"
      :scans="runScans"
      :ava-inspects="runAvaInspects"
      :snaps-loading="runSnapsLoading"
      :busy="runBusy"
      :error="runError"
      @run="startRun"
      @retry="runError = null" />
    <job-activity-dialog v-model="activityDialogOpen" :job="activityJob" @status-changed="reload" />
    <v-dialog :value="!!cancelConfirmJob" max-width="480" @input="!$event && settleCancellation(false)">
      <v-card ref="cancelConfirmationCard">
        <v-card-title id="cancel-analysis-title">Cancel analysis</v-card-title>
        <v-card-text>
          <p>Cancel the analysis for <strong>{{ cancelConfirmJob ? cancelConfirmJob.title : '' }}</strong>?</p>
          <p>Findings already saved will be kept. The job will stop processing further images.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="settleCancellation(false)">Keep running</v-btn>
          <v-btn text color="error" @click="settleCancellation(true)">Cancel analysis</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <jobs-table
      :groups="filteredTree"
      :loading="jobsLoading"
      :error="lastError"
      :has-any-jobs="allJobs.length > 0"
      :density="density"
      :run-disabled-reason="runBlockedReason"
      :cancel-states="cancelStates"
      @open="openJob"
      @activity="openActivity"
      @cancel="cancelJob"
      @run="onRun"
      @retry="reload"
      @clear-filters="clearFilters" />
  </v-sheet>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { error } from '@/utils/debugLogger';
  import { jobMatchesSearch } from '../utils/jobsFormat';
  import { cancellationErrorMessage, createJobCancellationController } from '../utils/jobCancellation';
  import { readDensity, storeDensity } from '../components/aiAnalysis/densityPreference';
  import JobsTopBar from '../components/aiAnalysis/JobsTopBar.vue';
  import JobsTable from '../components/aiAnalysis/JobsTable.vue';
  import JobActivityDialog from '../components/aiAnalysis/JobActivityDialog.vue';
  import RunAnalysisDialog, { RUN_MODE } from '../components/aiAnalysis/RunAnalysisDialog.vue';

  const DENSITY_STORAGE_KEY = 'aiva.aiAnalysis.jobsDensity';
  const RUN_STARTED_MESSAGE = 'Analysis started. The list refreshes itself while it runs.';
  const RUN_FAILED_FALLBACK = 'The analysis could not be started. Your selection is unchanged.';

  export default {
    name: 'AIAnalysisDashboard',
    components: { JobsTopBar, JobsTable, RunAnalysisDialog, JobActivityDialog },
    data() {
      return {
        runDialogOpen: false,
        activityDialogOpen: false,
        activityJob: null,
        cancelStates: {},
        cancelConfirmJob: null,
        runBusy: false,
        runError: null,
        runSnapsLoading: false,
        search: '',
        statusFilter: [],
        density: readDensity(DENSITY_STORAGE_KEY),
      };
    },
    computed: {
      runFacilityName() {
        const facility = this.$store.getters['FACILITY/GET_FACILITY'];
        return (facility && facility.name) || '';
      },
      runBlockedReason() {
        return this.$store.getters['AI_ANALYSIS/RUN_BLOCKED_REASON'];
      },
      /** The facility's 360 scans, with what the latest completed run found on each (user story 32). */
      runScans() {
        const latest = this.$store.getters['AI_ANALYSIS/LATEST_JOB_FOR_IMAGE'];
        return Object.values(this.$store.getters['AI_ANALYSIS/IMAGES_BY_ID'])
          .filter((i) => i && i.imageUrl)
          .map((i) => {
            const job = latest ? latest(i.id) : null;
            const count = job && job.status === 'Completed' && job.analysisResult ? job.analysisResult.numberOfAnomaliesFound : null;
            return { id: i.id, name: i.name || i.id, note: count != null ? `Analysed · ${count} ${count === 1 ? 'finding' : 'findings'}` : '' };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
      },
      /** AVA Inspect picker rows with their snap counts; snaps saved before image outlines existed must be retaken. */
      runAvaInspects() {
        const snapsFor = this.$store.getters['AI_ANALYSIS/SNAPS_FOR'];
        return (this.$store.getters['AI_ANALYSIS/AVA_INSPECT_OPTIONS'] || []).map((t) => {
          const snaps = snapsFor(t.id);
          return {
            ...t,
            snapCount: snaps.length,
            scanCount: new Set(snaps.map((s) => s.imageId)).size,
            retakeCount: snaps.filter((s) => s.hasImagePolygon === false).length,
          };
        });
      },
      ...mapGetters('AI_ANALYSIS', {
        jobTree: 'JOB_TREE',
        jobsLoading: 'JOBS_LOADING',
        runBlockedReason: 'RUN_BLOCKED_REASON',
        lastError: 'LAST_ERROR',
        hasRunningJobs: 'HAS_RUNNING_JOBS',
      }),
      facilityId() {
        return this.$route.params.id;
      },
      avaInspectTagsLoaded() {
        return Object.keys(this.$store.getters['AI_ANALYSIS/AVA_INSPECTS_BY_ID']).length > 0;
      },
      aiInspectImagesLoaded() {
        return (this.$store.getters['AI_INSPECT/IMAGES'] || []).length > 0;
      },
      allJobs() {
        return this.jobTree.flatMap((group) => group.children);
      },
      statusCounts() {
        return this.allJobs.reduce((acc, job) => ({ ...acc, [job.uiStatus]: (acc[job.uiStatus] || 0) + 1 }), {});
      },
      filteredTree() {
        const statuses = new Set(this.statusFilter);
        return this.jobTree
          .map((group) => ({
            ...group,
            children: group.children.filter((job) => (!statuses.size || statuses.has(job.uiStatus)) && jobMatchesSearch(job, group, this.search)),
          }))
          .filter((group) => group.children.length);
      },
    },
    watch: {
      facilityId() {
        this.invalidateCancellation();
        this.activityDialogOpen = false;
        this.activityJob = null;
        this.reload();
      },
      density(value) {
        storeDensity(DENSITY_STORAGE_KEY, value);
      },
      // First visit in a fresh session: the list may have loaded before the scan list. Under the mock the demo
      // jobs are seeded against scans, so an empty list is re-fetched once the scans arrive (one extra call on live).
      aiInspectImagesLoaded(loaded) {
        if (loaded && !this.allJobs.length && !this.jobsLoading) this.reload();
      },
    },
    created() {
      this.cancellationController = createJobCancellationController({
        getContext: () => this.facilityId,
        confirm: (job) => new Promise((resolve) => {
          if (this.cancelConfirmJob) return resolve(false);
          this.cancelConfirmJob = job;
          this.cancelConfirmationResolver = resolve;
          this.$nextTick(() => {
            const card = this.$refs.cancelConfirmationCard;
            const dialog = card && card.$el.closest('[role="dialog"]');
            if (dialog) dialog.setAttribute('aria-labelledby', 'cancel-analysis-title');
          });
        }),
        cancel: (job, facilityId) => this.$store.dispatch('AI_ANALYSIS/CANCEL_JOB', { id: job.id, facilityId }),
        onBusy: (id, phase) => {
          if (phase) this.$set(this.cancelStates, id, phase);
          else this.$delete(this.cancelStates, id);
        },
        onSuccess: (result) => {
          if (!result || !result.cancelled) return;
          if (result.refreshFailed) this.$dialog.notify.warning('Analysis cancelled. Findings already saved were kept. The list could not refresh; refresh it before starting another analysis.');
          else this.$dialog.notify.success('Analysis cancelled. Findings already saved were kept.');
        },
        onError: (err) => this.$dialog.notify.error(cancellationErrorMessage(err)),
      });
      this.reload();
      // Root action in the non-namespaced tags module (store/modules/tags.js), gated upstream by Tags.AvaInspect.View.
      if (!this.avaInspectTagsLoaded) this.$store.dispatch('FETCH_AVA_INSPECT_TAGS', { assetId: this.facilityId });
      // Scan names and sizes come from AI Inspect's image list on this route: root getImages (state.projects)
      // is only filled by Admin -> Projects. Same load AIInspect.vue performs.
      if (!this.aiInspectImagesLoaded) {
        const options = this.$store.getters['AI_INSPECT/GET_OPTIONS'];
        this.$store.commit('AI_INSPECT/SET_OPTIONS', { ...options, facilityId: this.facilityId });
        this.$store.dispatch('AI_INSPECT/GET_IMAGES');
      }
    },
    activated() {
      this.reload();
    },
    deactivated() {
      this.invalidateCancellation();
      this.activityDialogOpen = false;
    },
    beforeDestroy() {
      this.invalidateCancellation();
    },
    methods: {
      reload() {
        this.$store.dispatch('AI_ANALYSIS/LOAD_JOBS', this.facilityId);
      },
      clearFilters() {
        this.search = '';
        this.statusFilter = [];
      },
      async onRun() {
        this.runError = null;
        this.runDialogOpen = true;
        this.runSnapsLoading = true;
        try {
          await this.$store.dispatch('AI_ANALYSIS/LOAD_ALL_SNAPS');
        } finally {
          this.runSnapsLoading = false;
        }
      },
      async startRun(request) {
        this.runBusy = true;
        this.runError = null;
        try {
          if (request.mode === RUN_MODE.FULL_SCAN) {
            await this.$store.dispatch('AI_ANALYSIS/START_FULL_SCAN', { imagesIds: request.imagesIds });
          } else {
            await this.$store.dispatch('AI_ANALYSIS/START_AVA_INSPECT_RUN', { avaInspectId: request.avaInspectId });
          }
          this.runDialogOpen = false;
          this.$dialog.notify.success(RUN_STARTED_MESSAGE);
        } catch (err) {
          error('[AIAnalysis] start analysis failed', err);
          const server = err && err.response && err.response.data;
          this.runError = (typeof server === 'string' && server) || (server && server.message) || err.message || RUN_FAILED_FALLBACK;
        } finally {
          this.runBusy = false;
        }
      },
      openJob(job) {
        this.$router.push({ name: 'ai-analysis-job', params: { id: this.facilityId, jobId: job.id } });
      },
      openActivity(job) {
        this.activityJob = job;
        this.activityDialogOpen = true;
      },
      cancelJob(job) {
        return this.cancellationController.run(job);
      },
      settleCancellation(confirmed) {
        const resolve = this.cancelConfirmationResolver;
        this.cancelConfirmationResolver = null;
        this.cancelConfirmJob = null;
        if (resolve) resolve(confirmed);
      },
      invalidateCancellation() {
        if (this.cancellationController) this.cancellationController.invalidate();
        this.settleCancellation(false);
      },
    },
  };
</script>
